import { useApiClient, useFetchInfiniteES } from '@marageti/z4-lib';
import {
  FilterOp,
  SearchResult,
  FilterConstraint,
} from '@marageti/z4-sdk/lib/entity';
import { ChecklistItem, TripMatch, TripMatchStatus } from '@marageti/z4-sdk/lib/travel';
import { useMemo } from 'react';

/** Inclusive calendar-day range via Elasticsearch date math (`yyyy-MM-dd||/d`). */
function appendEsInclusiveDayRange(
  filterBy: FilterConstraint[],
  field: string,
  from: string,
  to: string,
) {
  const d0 = from?.trim();
  const d1 = to?.trim();
  if (d0) {
    filterBy.push({ field, op: FilterOp.FilterOpGTE, value: [`${d0}||/d`] });
  }
  if (d1) {
    filterBy.push({ field, op: FilterOp.FilterOpLTE, value: [`${d1}||/d`] });
  }
}

const refineSoldFilters: { [key: string]: FilterConstraint[] } = {
  preTravel: [{ field: 'departureDate', op: FilterOp.FilterOpGT, value: ['now'] }],
  inCountry: [
    { field: 'departureDate', op: FilterOp.FilterOpLTE, value: ['now'] },
    { field: 'returnDate', op: FilterOp.FilterOpGTE, value: ['now'] },
  ],
  requestReview: [
    { field: 'returnDate', op: FilterOp.FilterOpBetween, value: ['now-91d', 'now-2d'] },
    { field: 'checklist', value: [ChecklistItem.NeedsZicassoReviewRequest] },
  ],
  waitingForReview: [
    { field: 'returnDate', op: FilterOp.FilterOpBetween, value: ['now-91d', 'now-9d'] },
    { field: 'checklist', value: [ChecklistItem.NeedsZicassoReview] },
    { field: 'checklist', op: FilterOp.FilterOpNot, value: [ChecklistItem.NeedsZicassoReviewRequest] },
  ],
};

// selecting a single Canceled status chip should filter for any of the 5 Canceled statuses
export const canceledStatusesValue = 'CANCELED';
export const canceledStatuses: string[] = [
  TripMatchStatus.MatchZicassoCanceled,
  TripMatchStatus.MatchPartnerCanceled,
  TripMatchStatus.MatchTravelerCanceled,
  TripMatchStatus.MatchSoldCanceled,
  TripMatchStatus.MatchRefundedCanceled,
];

// departureDate and returnDate have optional 'ops' fields. The format for that is field=value|op
interface PossibleSearchFields {
  checklist?: string[];
  countries?: string[];
  departureDate?: string;
  /** Inclusive departure date filter (calendar day, `yyyy-MM-dd`). */
  departureDateFrom?: string;
  departureDateTo?: string;
  fullText?: string;
  /** Inclusive match-created filter on `createdAt` (`yyyy-MM-dd`). */
  matchDateFrom?: string;
  matchDateTo?: string;
  order?: string;
  partner?: string;
  refineSold?: string[];
  returnDate?: string;
  salesStage?: string[];
  sort?: string;
  status?: string[];
  traveler?: string;
  assignedTo?: string[];
}

const useTripMatchSearch = ({
  checklist = [],
  countries = [],
  departureDate = '',
  departureDateFrom = '',
  departureDateTo = '',
  fullText = '',
  matchDateFrom = '',
  matchDateTo = '',
  order = '',
  partner = 'all',
  refineSold = [],
  returnDate = '',
  salesStage = [],
  sort = '',
  status = [],
  traveler = '',
  assignedTo = [],
}: PossibleSearchFields) => {
  const apiClient = useApiClient();

  const getSearchRequest = () => {
    const filterBy: FilterConstraint[] = [];
    const sortBy: { field: string; order: string }[] = [];
    const doFullTextSearch = fullText.length > 2;

    if (partner && partner !== 'all' && partner !== 'none') {
      filterBy.push({ field: 'partnerId', value: [partner] });
    }

    if (traveler) {
      filterBy.push({ field: 'travelers.id', value: [traveler] });
    }

    if (countries.length) {
      filterBy.push({ field: 'countries', value: countries });
    }

    if (status.length) {
      if (status.includes(canceledStatusesValue)) {
        const statuses = [...status.filter((s) => s !== canceledStatusesValue), ...canceledStatuses];
        filterBy.push({ field: 'status', value: [...statuses] });
      } else {
        filterBy.push({ field: 'status', value: [...status] });
      }
    }

    if (salesStage.length) {
      filterBy.push({ field: 'salesStage', value: [...salesStage] });
    }

    if (checklist.length) {
      filterBy.push({ field: 'checklist', value: checklist });
    }

    appendEsInclusiveDayRange(filterBy, 'createdAt', matchDateFrom, matchDateTo);
    appendEsInclusiveDayRange(filterBy, 'departureDate', departureDateFrom, departureDateTo);

    if (departureDate) {
      filterBy.push({ field: 'departureDate', value: [departureDate] });
    }

    if (returnDate) {
      filterBy.push({ field: 'returnDate', value: [returnDate] });
    }

    if (refineSold.length) {
      refineSold.forEach((refine) => {
        filterBy.push(...refineSoldFilters[refine]);
      });
    }

    if (assignedTo?.length) {
      filterBy.push({ field: 'assignedTo', value: assignedTo });
    }

    if (sort && !doFullTextSearch) {
      sortBy.push({ field: sort, order });
    }

    return {
      filterBy,
      ...(sortBy.length > 0 ? { sortBy } : {}),
      fullText: doFullTextSearch ? fullText : undefined,
      phrase: fullText.includes('@'),
      pagination: { limit: 30, page: 1 },
    };
  };

  const searchRequest = getSearchRequest();

  const {
    data,
    error,
    hasMore,
    isLoading,
    loadMore,
    mutate,
  } = useFetchInfiniteES<TripMatch>(apiClient.tripMatchesClient.postTripMatchSearches, searchRequest, { revalidateOnFocus: false });

  const tripMatches = useMemo(() => (
    data?.flatMap((searchResult: SearchResult<TripMatch>) => searchResult.hits)
  ), [data]);

  return {
    tripMatches,
    error,
    hasMore,
    isLoading,
    loadMore,
    mutate,
    sortBy: searchRequest.sortBy ?? [],
    totalHits: data?.[0]?.totalHits || 0,
  };
};

export default useTripMatchSearch;
